import React, { useState, useEffect } from 'react';
import { bool, func, object, number, string } from 'prop-types';
import classNames from 'classnames';

import { FormattedMessage, intlShape } from '../../../../util/reactIntl';
import { ACCOUNT_SETTINGS_PAGES } from '../../../../routing/routeConfiguration';
import { propTypes, TRAVEL_EXPERT_USER_TYPE } from '../../../../util/types';
import {
  Avatar,
  InlineTextButton,
  LinkedLogo,
  Menu,
  MenuLabel,
  MenuContent,
  MenuItem,
  NamedLink,
  NotificationBadge,
} from '../../../../components';

import TopbarSearchForm from '../TopbarSearchForm/TopbarSearchForm';
import CustomLinksMenu from './CustomLinksMenu/CustomLinksMenu';

import css from './TopbarDesktop.module.css';
import { useSelector } from 'react-redux';
import { userTypeSelector } from '../../../../ducks/user.duck';
import AlgoliaSearchBar from '../../../AlgoliaSearchPage/AlgoliaSearchBar/AlgoliaSearchBar';
import { useParams } from 'react-router-dom/cjs/react-router-dom.min';

const cmsPages = [
  { pageId: 'how_it_works', messageId: 'TopbarDesktop.howWorks' },
  { pageId: 'top_destiations', messageId: 'TopbarDesktop.topDestinations' },
  { pageId: 'travel_tips_and_recommendations', messageId: 'TopbarDesktop.travelTips' },
  { pageId: 'travel_sustainably', messageId: 'TopbarDesktop.travelSustainably' },
  { pageId: 'travel_products_and_essentials', messageId: 'TopbarDesktop.travelProducts' },
  { pageId: 'our_partnerships', messageId: 'TopbarDesktop.ourPartnerships' },
];

const SignupLink = () => {
  return (
    <NamedLink name="SignupPage" className={css.topbarLink}>
      <span className={css.topbarLinkLabel}>
        <FormattedMessage id="TopbarDesktop.signup" />
      </span>
    </NamedLink>
  );
};

const LoginLink = () => {
  return (
    <NamedLink name="LoginPage" className={css.topbarLink}>
      <span className={css.topbarLinkLabel}>
        <FormattedMessage id="TopbarDesktop.login" />
      </span>
    </NamedLink>
  );
};

const InboxLink = ({ notificationCount, currentUserHasListings, inboxNotificationCount }) => {
  const notificationDot = inboxNotificationCount ? <div className={css.notificationDot} /> : null;
  const totalNotifications = inboxNotificationCount
  return (
    <NamedLink
      className={css.topbarLink}
      name="InboxPage"
      params={{ tab: 'inquiries' }}
    >
      <span className={css.topbarLinkLabel}>
        {/* total notifications count */}
        {totalNotifications > 0 ? <span className={css.notificationNumber}>{totalNotifications} </span> : null}
        <FormattedMessage id="TopbarDesktop.inbox" />
        {notificationDot}
      </span>
    </NamedLink>
  );
};

const ProfileMenu = ({ currentPage, currentUser, onLogout, params={}, inboxNotificationCount=0 }) => {
  const userType = useSelector(userTypeSelector);
  const currentPageClass = page => {
    const isAccountSettingsPage =
      page === 'AccountSettingsPage' && ACCOUNT_SETTINGS_PAGES.includes(currentPage);
    return currentPage === page || isAccountSettingsPage ? css.currentPage : null;
  };

  const menuItems = [
    {
      key: "ProfileSettingsPage",
      name: "ProfileSettingsPage",
      labelId: "TopbarDesktop.profileSettingsLink",
    },
    {
      key: "AccountSettingsPage",
      name: "AccountSettingsPage",
      labelId: "TopbarDesktop.accountSettingsLink",
    },
    ...(userType === TRAVEL_EXPERT_USER_TYPE
      ? [
        {
          key: "ManageListingsPage",
          name: "ManageListingsPage",
          labelId: "TopbarDesktop.yourListingsLink",
        },
      ]
      : []),
    {
      key: "InboxPage",
      name: "InboxPage",
      labelId: "TopbarDesktop.inboxham",
      params: {
        tab: 'inquiries'
      },
      inboxNotificationCount:inboxNotificationCount
    },
  ];
  const currentCMSPageClass = pageId => (pageId === params?.pageId ? css.currentPage : null);
  return (
    <Menu>
      <MenuLabel className={css.profileMenuLabel} isOpenClassName={css.profileMenuIsOpen}>
        <Avatar className={css.avatar} user={currentUser} disableProfileLink />
      </MenuLabel>
      <MenuContent className={css.profileMenuContent}>
        {menuItems.map(
          ({ key, name, labelId, params = {} , inboxNotificationCount }) => (
            <MenuItem key={key}>
              <NamedLink
                className={classNames(css.menuLink, currentPageClass(name))}
                name={name}
                params={params}
              >
                <span className={css.menuItemBorder} />
                <FormattedMessage id={labelId} />
                {inboxNotificationCount > 0 ? (
                  <NotificationBadge className={css.notificationBadge} count={inboxNotificationCount} />
                ) : ""}
              </NamedLink>
            </MenuItem>
          )
        )}
        {cmsPages.map(({ pageId, messageId }) => (
          <MenuItem key={pageId}>
            <NamedLink
              name="CMSPage"
              className={classNames(css.menuLink, currentCMSPageClass(pageId))}
              params={{ pageId }}
            >
              <span className={css.menuItemBorder} />
              <FormattedMessage id={messageId} />
            </NamedLink>
          </MenuItem>
        ))}
        <MenuItem key="logout">
          <InlineTextButton rootClassName={css.logoutButton} onClick={onLogout}>
            <span className={css.menuItemBorder} />
            <FormattedMessage id="TopbarDesktop.logout" />
          </InlineTextButton>
        </MenuItem>
      </MenuContent>
    </Menu>
  );
};



const ProfileNotSignedInMenu = ({ currentPage, params = {} }) => {
  const currentPageClass = page => {
    const isAccountSettingsPage =
      page === 'AccountSettingsPage' && ACCOUNT_SETTINGS_PAGES.includes(currentPage);
    return currentPage === page || isAccountSettingsPage ? css.currentPage : null;
  };

  const currentCMSPageClass = pageId => (pageId === params?.pageId ? css.currentPage : null);

  return (
    <Menu>
      <MenuLabel className={css.profileMenuLabel} isOpenClassName={css.profileMenuIsOpen}>
        <svg
          width="39"
          height="39"
          viewBox="0 0 39 39"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          aria-hidden="true"
          focusable="false"
        >
          <path
            d="M3.25 9.75C3.25 9.31902 3.4212 8.9057 3.72595 8.60095C4.0307 8.29621 4.44402 8.125 4.875 8.125H34.125C34.556 8.125 34.9693 8.29621 35.274 8.60095C35.5788 8.9057 35.75 9.31902 35.75 9.75C35.75 10.181 35.5788 10.5943 35.274 10.899C34.9693 11.2038 34.556 11.375 34.125 11.375H4.875C4.44402 11.375 4.0307 11.2038 3.72595 10.899C3.4212 10.5943 3.25 10.181 3.25 9.75ZM3.25 19.552C3.25 19.121 3.4212 18.7077 3.72595 18.403C4.0307 18.0982 4.44402 17.927 4.875 17.927H34.125C34.556 17.927 34.9693 18.0982 35.274 18.403C35.5788 18.7077 35.75 19.121 35.75 19.552C35.75 19.983 35.5788 20.3963 35.274 20.701C34.9693 21.0058 34.556 21.177 34.125 21.177H4.875C4.44402 21.177 4.0307 21.0058 3.72595 20.701C3.4212 20.3963 3.25 19.983 3.25 19.552ZM4.875 27.7306C4.44402 27.7306 4.0307 27.9018 3.72595 28.2066C3.4212 28.5113 3.25 28.9246 3.25 29.3556C3.25 29.7866 3.4212 30.1999 3.72595 30.5047C4.0307 30.8094 4.44402 30.9806 4.875 30.9806H34.125C34.556 30.9806 34.9693 30.8094 35.274 30.5047C35.5788 30.1999 35.75 29.7866 35.75 29.3556C35.75 28.9246 35.5788 28.5113 35.274 28.2066C34.9693 27.9018 34.556 27.7306 34.125 27.7306H4.875Z"
            fill="white"
          />
        </svg>
      </MenuLabel>

      <MenuContent className={css.profileMenuContent}>
        {cmsPages.map(({ pageId, messageId }) => (
          <MenuItem key={pageId}>
            <NamedLink
              name="CMSPage"
              className={classNames(css.menuLink, currentCMSPageClass(pageId))}
              params={{ pageId }}
            >
              <span className={css.menuItemBorder} />
              <FormattedMessage id={messageId} />
            </NamedLink>
          </MenuItem>
        ))}

        <MenuItem key="Login">
          <NamedLink
            name="LoginPage"
            className={classNames(currentPageClass('Login'))}
          >
            <InlineTextButton rootClassName={css.logoutButton}>
              <span className={css.menuItemBorder}>
              </span>
              <FormattedMessage id="TopbarDesktop.login" />
            </InlineTextButton>
          </NamedLink>
          <br />


        </MenuItem>
      </MenuContent>
    </Menu>
  );
};


const TopbarDesktop = props => {
  const {
    className,
    config,
    customLinks,
    currentUser,
    currentPage,
    rootClassName,
    currentUserHasListings,
    notificationCount,
    intl,
    isAuthenticated,
    onLogout,
    onSearchSubmit,
    initialSearchFormValues,
    routeConfiguration,
    history,
    inboxNotificationCount
  } = props;

  const params = useParams();
  const [mounted, setMounted] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    setMounted(true);

    const handleScroll = () => {
      if (window.scrollY > 300) {
        setScrolled(true);
      } else {
        setScrolled(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const marketplaceName = config.marketplaceName;
  const authenticatedOnClientSide = mounted && isAuthenticated;
  const isAuthenticatedOrJustHydrated = isAuthenticated || !mounted;
  const giveSpaceForSearch = customLinks == null || customLinks?.length === 0;
  const classes = classNames(
    rootClassName || css.root,
    className,
    (currentPage == "LandingPage" || currentPage == "RegionLandingPage" || currentPage == "CountryLandingPage") && css.landingpageTopbar,
    (currentPage == "LandingPage" || currentPage == "RegionLandingPage" || currentPage == "CountryLandingPage") && scrolled && css.scrolledTopbar
  );

  const inboxLinkMaybe = authenticatedOnClientSide ? (
    <InboxLink
      notificationCount={notificationCount}
      currentUserHasListings={currentUserHasListings}
      inboxNotificationCount={inboxNotificationCount}
    />
  ) : null;

  const profileMenuMaybe = authenticatedOnClientSide ? (
    <ProfileMenu currentPage={currentPage} currentUser={currentUser} onLogout={onLogout} params={params} inboxNotificationCount={inboxNotificationCount} />
  ) : <ProfileNotSignedInMenu currentPage={currentPage} params={params} />;

  const CTAMaybe = ['ManageListingsPage', 'searchPage'].includes(
    currentPage
  ) ? null : currentUser ? (
    <NamedLink name="SearchPage" className={css.BecomeTraverExpert}>
      <FormattedMessage id="TopbarMobileMenu.discoverTravelExperts" />
    </NamedLink>
  ) : (
    <NamedLink
      name="SignupForUserTypePage"
      params={{ userType: TRAVEL_EXPERT_USER_TYPE }}
      className={css.BecomeTraverExpert}
    >
      <span>{intl.formatMessage({ id: "TopbarDesktop.becomeTravelExpert" })}</span>
    </NamedLink>
  );

  const signupLinkMaybe = isAuthenticatedOrJustHydrated ? null : <SignupLink />;
  const loginLinkMaybe = isAuthenticatedOrJustHydrated ? null : <LoginLink />;

  return (
    <nav className={classes}>
      <div className={css.leftSection}>
        <LinkedLogo
          className={css.logoLink}
          layout="desktop"
          alt={intl.formatMessage({ id: 'TopbarDesktop.logo' }, { marketplaceName })}
          linkToExternalSite={config?.topbar?.logoLink}
        />
        {['LandingPage', 'RegionLandingPage', 'CountryLandingPage', 'AlgoliaSearchPage'].includes(currentPage) ? null : (
          <AlgoliaSearchBar
            routeConfiguration={routeConfiguration}
            history={history}
            isTopbarSearch={true}
          />
        )}
      </div>
      {/* <TopbarSearchForm
          className={classNames(css.searchLink, { [css.takeAvailableSpace]: giveSpaceForSearch })}
          desktopInputRoot={css.topbarSearchWithLeftPadding}
          onSubmit={onSearchSubmit}
          initialValues={initialSearchFormValues}
          appConfig={config}
        /> */}

      {/* <CustomLinksMenu
        currentPage={currentPage}
        customLinks={customLinks}
        intl={intl}
        hasClientSideContentReady={authenticatedOnClientSide || !isAuthenticatedOrJustHydrated}
      /> */}
      <div className={css.topbarRight}>
        {CTAMaybe}

        {inboxLinkMaybe}
        {signupLinkMaybe}
        {profileMenuMaybe}
      </div>
      {/* {loginLinkMaybe} */}
    </nav>
  );
};

TopbarDesktop.defaultProps = {
  rootClassName: null,
  className: null,
  currentUser: null,
  currentPage: null,
  notificationCount: 0,
  initialSearchFormValues: {},
  config: null,
};

TopbarDesktop.propTypes = {
  rootClassName: string,
  className: string,
  currentUserHasListings: bool.isRequired,
  currentUser: propTypes.currentUser,
  currentPage: string,
  isAuthenticated: bool.isRequired,
  onLogout: func.isRequired,
  notificationCount: number,
  onSearchSubmit: func.isRequired,
  initialSearchFormValues: object,
  intl: intlShape.isRequired,
  config: object,
};

export default TopbarDesktop;
